import type { ReactNode } from "react";
import { NavLink } from "react-router-dom";
import { Icon } from "@iconify/react";
import { cn } from "@/shared/lib/utils";
import { Button } from "@/shared/components/button";
import { Tooltip } from "@/shared/components/misc";

export interface SidebarNavItem {
  path: string;
  label: string;
  icon: string;
  end?: boolean;
}

export interface SidebarNavSection {
  title: string;
  items: SidebarNavItem[];
}

interface PortalSidebarProps {
  logoTitle: ReactNode;
  logoSubtitle: string;
  profileSlot?: ReactNode;
  navSections: SidebarNavSection[];
  onSettingsClick?: () => void;
  onSignOut: () => void;
}

function NavSection({ title, items }: SidebarNavSection) {
  return (
    <>
      <p className="mb-1.5 mt-8 px-3 text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF] first:mt-0">
        {title}
      </p>
      <div className="space-y-0.5">
        {items.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.end}
            // Cross-fades the routed <main> content on navigation via the View
            // Transitions API (scoped in index.css so only <main> animates).
            viewTransition
            // State is driven off the `aria-current="page"` attribute NavLink sets
            // on the active link, not a render-prop, so the icon/label stay mounted
            // across a navigation and only their styles change. Only background-color
            // is transitioned — text/icon color snaps — so the incoming link never
            // flashes its dark inactive text while the red highlight fades in.
            className={cn(
              "group flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium",
              "text-[#3A3D42] transition-[background-color] duration-200 ease-out motion-reduce:transition-none",
              "hover:bg-[#F5F5F5]",
              "aria-[current=page]:bg-[#E4002B80] aria-[current=page]:text-white",
            )}
          >
            <Icon
              icon={item.icon}
              className="h-4 w-4 shrink-0 text-[#111827] group-aria-[current=page]:text-white"
            />
            {item.label}
          </NavLink>
        ))}
      </div>
    </>
  );
}

export function PortalSidebar({
  logoTitle,
  logoSubtitle,
  profileSlot,
  navSections,
  onSettingsClick,
  onSignOut,
}: PortalSidebarProps) {
  return (
    <aside className="flex h-screen w-64 flex-col rounded-tr-3xl rounded-br-3xl border-r border-[#E5E7EB] bg-white shadow-[6px_0_24px_0_rgba(0,0,0,0.12)]">
      <div className="flex h-[73px] items-center gap-3 border-b border-[#E5E7EB] px-5">
        <img src="/logo.png" alt="Logo" className="h-10 w-10 rounded-lg object-contain" />
        <div className="flex flex-col gap-0">
          <h1 className="text-lg leading-tight tracking-tight text-[#111827]" style={{ fontWeight: 900, letterSpacing: "-0.02em" }}>
            {logoTitle}
          </h1>
          <p className="text-[11px] font-medium leading-tight text-[#4B5563]">{logoSubtitle}</p>
        </div>
      </div>

      {profileSlot && <div className="border-b border-[#E5E7EB]">{profileSlot}</div>}

      <nav className="min-h-0 flex-1 overflow-y-auto px-3 py-4">
        {navSections.map((section) => (
          <NavSection key={section.title} title={section.title} items={section.items} />
        ))}
      </nav>

      <div className="shrink-0 border-t border-[#E5E7EB] p-2">
        <div className="flex items-center gap-1">
          <Tooltip title="Settings" placement="top">
            <Button
              variant="ghost"
              size="icon"
              className="flex items-center justify-center hover:!bg-[#F5F5F5]"
              onClick={onSettingsClick}
            >
              <Icon icon="lucide:settings" className="h-4 w-4 text-[#111827]" />
            </Button>
          </Tooltip>
          <Tooltip title="Logout" placement="top">
            <Button
              variant="ghost"
              size="icon"
              className="flex items-center justify-center hover:!bg-[#F5F5F5]"
              onClick={onSignOut}
            >
              <Icon icon="lucide:log-out" className="h-4 w-4 text-[#111827]" />
            </Button>
          </Tooltip>
        </div>
      </div>
    </aside>
  );
}
